// src/components/DimensionsTab.js - REFACTORED FOR BASE64
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Slider,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils'; // Import the utility
import { convertPdfToImages } from '../utils/pdfUtils';
import api from '../api';

const DimensionsTab = () => {
    const { state, actions } = useFormContext();

    const localState = state.dimensionsPdfState;

    const [imageRef, setImageRef] = useState(null);
    const canvasRef = useRef(null);

    const [localCrop, setLocalCrop] = useState({ unit: '%', width: 30, height: 30, x: 25, y: 25 });
    const [localCompletedCrop, setLocalCompletedCrop] = useState(null);

    // This effect is still needed to sync the final cropped images into the main dimensionsData
    useEffect(() => {
        const dataToSend = {
            pdfFile: localState.pdfFile,
            croppedImage1: localState.croppedImageFile1, // This will now be our { base64, ... } object
            croppedImage2: localState.croppedImageFile2,
        };
        actions.setDimensionsData(dataToSend);
    }, [localState.pdfFile, localState.croppedImageFile1, localState.croppedImageFile2, actions]);

    // Reset crop when image changes
    useEffect(() => {
        setLocalCrop({ unit: '%', width: 30, height: 30, x: 25, y: 25 });
        setLocalCompletedCrop(null);
    }, [localState.currentImageIndex, localState.convertedImages]);

    const handlePdfFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            actions.updateDimensionsPdfState({
                pdfFile: file,
                loading: true,
                error: null,
                convertedImages: [],
                currentImageIndex: 0,
                croppedImageUrl1: null,
                croppedImageUrl2: null,
                croppedImageFile1: null,
                croppedImageFile2: null
            });

            try {
                const images = await convertPdfToImages(file);
                actions.updateDimensionsPdfState({ convertedImages: images, loading: false });
            } catch (err) {
                console.error('Error extracting PDF images:', err);
                actions.updateDimensionsPdfState({
                    error: 'Failed to extract PDF images. Please ensure file is a valid PDF.',
                    loading: false
                });
            }
        }
    };

    const handleRemovePdf = () => {
        actions.updateDimensionsPdfState({
            pdfFile: null,
            convertedImages: [],
            currentImageIndex: 0,
            croppedImageUrl1: null,
            croppedImageUrl2: null,
            croppedImageFile1: null,
            croppedImageFile2: null,
            error: null
        });
    };

    const handleSliderChange = (event, newValue) => {
        actions.updateDimensionsPdfState({ currentImageIndex: newValue });
    };

    const onImageLoad = (e) => setImageRef(e.target);

    const getCroppedBlob = (image, cropData) => {
        const canvas = canvasRef.current;
        if (!canvas || !image) return Promise.resolve(null);

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = cropData.width * scaleX;
        canvas.height = cropData.height * scaleY;
        const ctx = canvas.getContext('2d');

        if (!ctx) return Promise.resolve(null);

        ctx.drawImage(image, cropData.x * scaleX, cropData.y * scaleY, cropData.width * scaleX, cropData.height * scaleY, 0, 0, cropData.width * scaleX, cropData.height * scaleY);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
        });
    };

    const handleCropImage = async (imageNumber) => {
        const cropToUse = localCompletedCrop || localCrop;
        if (imageRef && cropToUse?.width > 0 && cropToUse?.height > 0) {
            const croppedBlob = await getCroppedBlob(imageRef, cropToUse);
            if (croppedBlob) {
                try {
                    const base64 = await fileToBase64(croppedBlob);
                    const fileName = `cropped_image_${imageNumber}.png`;
                    const fileData = { base64, name: fileName, type: 'image/png' };

                    // Store the persistable object and use the base64 string for the preview URL
                    actions.updateDimensionsPdfState({
                        [`croppedImageFile${imageNumber}`]: fileData,
                        [`croppedImageUrl${imageNumber}`]: base64
                    });
                } catch (error) {
                    console.error('Error converting cropped blob to Base64:', error);
                }
            } else {
                console.error('Failed to crop image - no blob data available');
            }
        } else {
            console.error('Invalid crop parameters:', { imageRef, cropToUse });
        }
    };

    const handleRemoveCroppedImage = (imageNumber) => {
        actions.updateDimensionsPdfState({
            [`croppedImageUrl${imageNumber}`]: null,
            [`croppedImageFile${imageNumber}`]: null
        });
    };

    if (!state.patientData) {
        return (
            <Box p={3}>
                <Typography variant="h6" color="textSecondary">Please load patient data first.</Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>Dimensions</Typography>

            <Grid container spacing={3}>
                {/* PDF Selection Card */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1">Select length and width PDF file</Typography>
                                {localState.pdfFile && (
                                    <IconButton size="small" color="error" onClick={handleRemovePdf} title="Remove PDF">
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                            <Button variant="contained" component="label" sx={{ mb: 2 }}>
                                {localState.pdfFile ? 'Replace PDF' : 'Select PDF'}
                                <input hidden type="file" onChange={handlePdfFileChange} accept=".pdf" />
                            </Button>
                            {localState.pdfFile && (
                                <Typography variant="caption" display="block" color="success.main">
                                    Selected: {localState.pdfFile.name}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Loading and Error States */}
                {localState.loading && <Grid item xs={12}><Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /><Typography sx={{ ml: 2 }}>Converting PDF...</Typography></Box></Grid>}
                {localState.error && <Grid item xs={12}><Alert severity="error" sx={{ mt: 2 }}>{localState.error}</Alert></Grid>}

                {/* Cropping UI and Previews */}
                {localState.convertedImages.length > 0 && (
                    <React.Fragment>
                        <Grid item xs={12} md={8}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography gutterBottom>Page {localState.currentImageIndex + 1} of {localState.convertedImages.length}</Typography>
                                    <Slider value={localState.currentImageIndex} onChange={handleSliderChange} min={0} max={localState.convertedImages.length - 1} step={1} marks valueLabelDisplay="auto" sx={{ mb: 2 }} />
                                    <Box position="relative">
                                        <ReactCrop crop={localCrop} onChange={setLocalCrop} onComplete={setLocalCompletedCrop} key={localState.currentImageIndex}>
                                            <img src={`data:image/png;base64,${localState.convertedImages[localState.currentImageIndex]}`} alt={`Page ${localState.currentImageIndex + 1}`} style={{ maxWidth: '100%', height: 'auto' }} onLoad={onImageLoad} />
                                        </ReactCrop>
                                        {localCompletedCrop && imageRef && (
                                            <Box style={{ position: 'absolute', top: `${localCompletedCrop.y + localCompletedCrop.height + 5}px`, left: `${localCompletedCrop.x}px`, display: 'flex', gap: '5px' }}>
                                                <Button variant="contained" size="small" onClick={() => handleCropImage(1)}>SP</Button>
                                                <Button variant="contained" size="small" onClick={() => handleCropImage(2)}>SA</Button>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Grid container spacing={2}>
                                {[1, 2].map(num => (
                                    <Grid item xs={12} key={num}>
                                        <Card variant="outlined" sx={{ height: '320px' }}>
                                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="subtitle1">{num === 1 ? 'Scale Panoramic View' : 'Scale Axial View'}</Typography>
                                                    {localState[`croppedImageFile${num}`] && (
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveCroppedImage(num)} title="Remove image">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <Box sx={{ flexGrow: 1, border: '2px dashed #ccc', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', overflow: 'hidden' }}>
                                                    {localState[`croppedImageUrl${num}`] ? (
                                                        <img src={localState[`croppedImageUrl${num}`]} alt={`Preview ${num}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">No image set</Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ height: 22, mt: 1, overflow: 'hidden' }}>
                                                    {localState[`croppedImageFile${num}`] && (
                                                        <Typography variant="caption" color="success.main" noWrap>{localState[`croppedImageFile${num}`].name}</Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </React.Fragment>
                )}
            </Grid>

            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
    );
};

export default DimensionsTab;